import React, { useContext } from 'react'
import '../EnviromentalSafeguards/styles.scss'
import usePortfolioName from '../../../../../hooks/usePortfolioName';
import { TagFilterContext } from '../../../../../context/TagFilterContext';
import HeaderExtended from '../HeaderExtended';
import { useTranslation } from 'react-i18next';
import { Bar, Doughnut } from 'react-chartjs-2';
import CountProjects from '../shared/CountProjects';
import ProjectAvatar from '../shared/ProjectAvatar';
import { EnviromentalSafeguardsData } from '../GeneralInformation/typesExtendedReport';
import { useEnvSafeguardsData } from './useEnvSafeguardsData';
import EnvironmentalSocialCategory from './components/EnvironmentalSocialCategory';
import EnvironmentalSocialRisk from './components/EnvironmentalSocialRisk';
import EnvironmentalSocialPerformance from './components/EnvironmentalSocialPerformance';
import ProjectsWithMICI from './components/ProjectsWithMICI';
import ReusableIcon from '../shared/ReusableIcon';


type safeguardsProps = {
    data: EnviromentalSafeguardsData;
    isLoan?: boolean;
    isTcp?: boolean;
    isIgr?: boolean;
    creationDate?: any;
    regions?: any;
    countries?: string;
    myPortfolio?: string;
    filtersApplied?: any;
}

const COLORSmore3mm = {
    a: '#416DBC',
    b: '#00CFDD',
    fi: '#91E2A1',
    b13: '#F7DD00',
    c: '#FDAC41',
    na: '#D0DEF8'
}

const COLORSbars = {
    red: '#F06D6D',
    orange: '#EF9206',
    yellow: '#E7CE00',
    green: '#6DD983',
    na: '#D0DEF8'
}

const COLORSperformanceRatingSocial = {
    red: '#E92C2C',
    orange: '#FF8C26',
    lightGreen: '#C7EAA3',
    green: '#50AC32',
    na: '#D0DEF8'
}

const optionsSafeguards = {
    maintainAspectRatio: false,
    responsive: true,
    legend: {
        display: false,
    },
    plugins: {
        datalabels: false
    },
    scales: {
        yAxes: [
            {
                ticks: {
                    beginAtZero: true,
                    fontSize: 12,
                },
                gridLines: {
                    drawBorder: false,
                },
            },
        ],
        xAxes: [
            {
                gridLines: {
                    display: false,
                    drawBorder: true,
                },
            },
        ],
    },
};

const doughnutoptions = {
    cutoutPercentage: 83,
    responsive: true,
    maintainAspectRatio: false,
    legend: {
        display: false
    },
    plugins: {
        datalabels: false
    }
};

const optionsPerformanceRatingSocial = {
    maintainAspectRatio: false,
    responsive: true,
    legend: {
        display: false,
    },
    plugins: {
        datalabels: false
    },
    scales: {
        yAxes: [
            {
                ticks: {
                    beginAtZero: true,
                    fontSize: 12,
                },
                gridLines: {
                    drawBorder: false,
                },
            },
        ],
        xAxes: [
            {
                gridLines: {
                    display: false,
                    drawBorder: true,
                },
            },
        ],
    },
};

const EnviromentalSafeguards: React.FC<safeguardsProps> = ({ data, isLoan, isTcp, isIgr, creationDate, regions, countries, myPortfolio, filtersApplied }) => {
    const { t } = useTranslation();
    const portfolioName = usePortfolioName();
    const { labels } = useContext(TagFilterContext);
    const titleLon = 'LON';
    const titleTcp = 'TCP';
    const titleIgr = 'IGR';
    const mp = useEnvSafeguardsData(data);
    if (!data || !mp) return null;

    const more3mmLegend = [
        { label: `A`, value: mp.EsgDistributionAccording.a, color: COLORSmore3mm.a },
        { label: `B`, value: mp.EsgDistributionAccording.b, color: COLORSmore3mm.b },
        { label: `FI (Bajo ESPF)`, value: mp.EsgDistributionAccording.fi, color: COLORSmore3mm.fi },
        { label: `B13 (Bajo OP-703)`, value: mp.EsgDistributionAccording.b13, color: COLORSmore3mm.b13 },
        { label: `C`, value: mp.EsgDistributionAccording.c, color: COLORSmore3mm.c },
        { label: `${t('extended_report.not_asigned')}`, value: mp.EsgDistributionAccording.noT_ASSIGNED, color: COLORSmore3mm.na },
    ];

    const socialRiskRatingLegend = [
        { label: `${t('extended_report.safeg_high')}`, value: mp.EsgEnvironmentalSocialRiskRating.highRed, color: COLORSbars.red },
        { label: `${t('extended_report.safeg_substantial')}`, value: mp.EsgEnvironmentalSocialRiskRating.substantialOrange, color: COLORSbars.orange },
        { label: `${t('extended_report.safeg_moderate')}`, value: mp.EsgEnvironmentalSocialRiskRating.moderateYellow, color: COLORSbars.yellow },
        { label: `${t('extended_report.safeg_low')}`, value: mp.EsgEnvironmentalSocialRiskRating.lowGreen, color: COLORSbars.green },
        { label: `${t('extended_report.not_asigned')}`, value: mp.EsgEnvironmentalSocialRiskRating.not_AssignedGray, color: COLORSbars.na },
    ];

    const performanceRatingSocialLegend = [
        { label: `${t('one_pager_report.satisfactory')}`, value: mp.EsgSafeGuardPerformanceRatingSocial.satisfactory, color: COLORSperformanceRatingSocial.green },
        { label: `${t('extended_report.partially_satisfactory')}`, value: mp.EsgSafeGuardPerformanceRatingSocial.partially_Satisfactory, color: COLORSperformanceRatingSocial.lightGreen },
        { label: `${t('extended_report.unsatisfactory')}`, value: mp.EsgSafeGuardPerformanceRatingSocial.unsatisfactory, color: COLORSperformanceRatingSocial.red },
        { label: `${t('extended_report.partially_unsatisfactory')}`, value: mp.EsgSafeGuardPerformanceRatingSocial.partially_Unsatisfactory, color: COLORSperformanceRatingSocial.orange },
        { label: `${t('extended_report.not_asigned')}`, value: mp.EsgSafeGuardPerformanceRatingSocial.not_Assigned, color: COLORSperformanceRatingSocial.na },
    ];

    const doughnutData = {
        labels: [],
        datasets: [
            {
                data: [
                    mp.EsgDistributionAccording.a || 0,
                    mp.EsgDistributionAccording.b || 0,
                    mp.EsgDistributionAccording.fi || 0,
                    mp.EsgDistributionAccording.b13 || 0,
                    mp.EsgDistributionAccording.c || 0,
                    mp.EsgDistributionAccording.noT_ASSIGNED || 0
                ],
                backgroundColor: [COLORSmore3mm.a, COLORSmore3mm.b, COLORSmore3mm.fi, COLORSmore3mm.b13, COLORSmore3mm.c, COLORSmore3mm.na],
            },
        ],
    };

    const dataSafeguards = {
        labels: [" ", " ", " ", " ", " "],
        datasets: [
            {
                label: "Valores",
                data: [
                    mp.EsgEnvironmentalSocialRiskRating.highRed || null,
                    mp.EsgEnvironmentalSocialRiskRating.substantialOrange || null,
                    mp.EsgEnvironmentalSocialRiskRating.moderateYellow || null,
                    mp.EsgEnvironmentalSocialRiskRating.lowGreen || null,
                    mp.EsgEnvironmentalSocialRiskRating.not_AssignedGray || null
                ],
                backgroundColor: [
                    COLORSbars.red,
                    COLORSbars.orange,
                    COLORSbars.yellow,
                    COLORSbars.green,
                    COLORSbars.na,
                ],
                borderWidth: 0,
                barThickness: 20,
            },
        ],
    };

    const dataperformanceRatingSocial = {
        labels: [" ", " ", " ", " ", " "],
        datasets: [
            {
                label: "Valores",
                data: [
                    mp.EsgSafeGuardPerformanceRatingSocial.satisfactory || null,
                    mp.EsgSafeGuardPerformanceRatingSocial.partially_Satisfactory || null,
                    mp.EsgSafeGuardPerformanceRatingSocial.unsatisfactory || null,
                    mp.EsgSafeGuardPerformanceRatingSocial.partially_Unsatisfactory || null,
                    mp.EsgSafeGuardPerformanceRatingSocial.not_Assigned || null
                ],
                backgroundColor: [
                    COLORSperformanceRatingSocial.green,
                    COLORSperformanceRatingSocial.lightGreen,
                    COLORSperformanceRatingSocial.red,
                    COLORSperformanceRatingSocial.orange,
                    COLORSperformanceRatingSocial.na,
                ],
                borderWidth: 0,
                barThickness: 20,
            },
        ],
    };

    return (

        <>
            <div className='safe-content c-main'>
                <HeaderExtended
                    portfolioName={portfolioName}
                    filtersApplied={filtersApplied ?? labels}
                    creationDate={creationDate}
                    regions={regions}
                    countries={countries}
                    myPortfolio={myPortfolio}
                />
                <div className='safe-content-background'>
                    <header className="header-content safe-content-header mt-2">
                        <p>{t('extended_report.environmental_and_social_safeguards')}</p>
                        <div className='flex-avatars'>
                            {(isLoan) && (<ReusableIcon title={titleLon} color="#BE86D8" />)}
                            {(isTcp) && (<ReusableIcon title={titleTcp} color="#66B6B6" />)}
                            {(isIgr) && (<ReusableIcon title={titleIgr} color="#F1AF43" />)}
                        </div>
                    </header>

                    <div className="grid-safeguards">
                        <section className="panel-safeguards">
                            <EnvironmentalSocialCategory
                                more3mmLegend={more3mmLegend}
                                doughnutData={doughnutData}
                                doughnutoptions={doughnutoptions}
                            />
                        </section>

                        <section className="panel-safeguards">
                            <EnvironmentalSocialRisk
                                socialRiskRatingLegend={socialRiskRatingLegend}
                                dataSafeguards={dataSafeguards}
                                optionsSafeguards={optionsSafeguards}
                            />
                        </section>

                        <section className="panel-safeguards">
                            <EnvironmentalSocialPerformance
                                performanceRatingSocialLegend={performanceRatingSocialLegend}
                                dataperformanceRatingSocial={dataperformanceRatingSocial}
                                optionsPerformanceRatingSocial={optionsPerformanceRatingSocial}
                            />
                        </section>

                        <section className="panel-safeguards">
                            <ProjectsWithMICI
                                mp={mp}
                                isLoan={isLoan}
                                isTcp={isTcp}
                                isIgr={isIgr}
                                titleLon={titleLon}
                                titleTcp={titleTcp}
                                titleIgr={titleIgr}
                            />
                        </section>
                    </div>
                </div>
            </div>
        </>
    )
}

export default EnviromentalSafeguards